import { Badge, Button, Callout, useToast } from '@apovisio/ui';
import type { LivekitConnection } from '@apovisio/contracts';
import {
  CarouselLayout,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LiveKitRoom,
  MediaDeviceMenu,
  ParticipantTile,
  RoomAudioRenderer,
  StartAudio,
  TrackToggle,
  isTrackReference,
  useConnectionQualityIndicator,
  useConnectionState,
  useLocalParticipant,
  useParticipants,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { ConnectionQuality, ConnectionState, DisconnectReason, Track } from 'livekit-client';
import type { MediaDeviceFailure } from 'livekit-client';
import { MessageSquare, PhoneOff, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { PageHeading } from '../../components/PageHeading';
import type { DevicePreferences } from '../guest/device-preferences';
import {
  MEDIA_PROBLEM_MESSAGES,
  MEDIA_PROBLEM_REASONS,
  classifyMediaError,
} from '../guest/media-errors';
import { reportClientError } from '../../lib/telemetry';
import { ChatPanel, ChatProvider, useRoomChat } from './ChatPanel';

export interface RoomViewProps {
  connection: LivekitConnection;
  title: string;
  participantRole: 'host' | 'guest';
  chatEnabled: boolean;
  screenShareEnabled: boolean;
  devices: DevicePreferences;
  /** Panneau supplémentaire (salle d'attente de l'hôte) et son libellé de bouton. */
  sidePanel?: { label: string; content: (close: () => void) => ReactNode };
  /** Clic sur « Quitter » ; reçoit les identités des autres participants présents. */
  onLeave: (context: { remoteIdentities: string[] }) => void;
  onDisconnected: (reason: DisconnectReason | undefined) => void;
}

const canShareScreen = (): boolean =>
  typeof navigator !== 'undefined' &&
  'mediaDevices' in navigator &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function';

/**
 * Écran 8 — Salle de visio : grille sobre, barre de contrôle réduite (composants LiveKit),
 * panneau latéral uniquement si le chat est activé (et salle d'attente pour l'hôte).
 */
export function RoomView(props: RoomViewProps) {
  const toast = useToast();
  const { connection, devices } = props;
  const options = useMemo(
    () => ({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: devices.videoDeviceId ? { deviceId: devices.videoDeviceId } : {},
      audioCaptureDefaults: devices.audioDeviceId ? { deviceId: devices.audioDeviceId } : {},
    }),
    [devices.videoDeviceId, devices.audioDeviceId],
  );

  return (
    <LiveKitRoom
      serverUrl={connection.serverUrl}
      token={connection.token}
      connect
      video={devices.videoEnabled}
      audio={devices.audioEnabled}
      options={options}
      data-lk-theme="default"
      className="av-theme-room app-room"
      onDisconnected={props.onDisconnected}
      onMediaDeviceFailure={(failure?: MediaDeviceFailure) => {
        const problem = classifyMediaError(failure);
        if (problem !== 'other') reportClientError(MEDIA_PROBLEM_REASONS[problem]);
        const message = MEDIA_PROBLEM_MESSAGES[problem];
        toast.show({ tone: 'warning', title: message.title, description: message.body });
      }}
    >
      {props.chatEnabled ? (
        <ChatProvider>
          <RoomContent {...props} />
        </ChatProvider>
      ) : (
        <RoomContent {...props} />
      )}
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}

function RoomContent({
  title,
  participantRole,
  chatEnabled,
  screenShareEnabled,
  sidePanel,
  onLeave,
}: RoomViewProps) {
  const [panel, setPanel] = useState<'chat' | 'side' | null>(null);
  const chat = useRoomChat();
  const connectionState = useConnectionState();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const { quality } = useConnectionQualityIndicator({ participant: localParticipant });
  const close = () => {
    setPanel(null);
  };
  const degraded = quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost;

  useEffect(() => {
    if (connectionState === ConnectionState.Reconnecting) reportClientError('network_lost');
  }, [connectionState]);

  useEffect(() => {
    if (degraded) reportClientError('network_degraded');
  }, [degraded]);

  return (
    <div className="app-room__layout">
      <header className="app-room__top">
        <div className="av-stack av-stack--sm">
          <PageHeading documentTitle={title} className="app-room__title">
            {title}
          </PageHeading>
          <span className="av-text-secondary av-text-sm">
            {participants.length} participant{participants.length > 1 ? 's' : ''}
          </span>
        </div>
        {participantRole === 'host' ? <Badge tone="info">Vous êtes organisateur</Badge> : null}
      </header>

      <div role="status" className="app-room__banners">
        {connectionState === ConnectionState.Reconnecting ? (
          <Callout tone="warning" title="Connexion perdue. Reconnexion en cours…" />
        ) : connectionState === ConnectionState.Connecting ? (
          <Callout tone="info" title="Connexion à la salle…" />
        ) : degraded ? (
          <Callout tone="warning" title="Connexion instable : la vidéo peut être dégradée." />
        ) : null}
      </div>

      <div className="app-room__body">
        <RoomStage />
        {panel === 'chat' && chatEnabled ? <ChatPanel onClose={close} /> : null}
        {panel === 'side' && sidePanel ? (
          <aside className="app-room__panel" aria-label={sidePanel.label}>
            {sidePanel.content(close)}
          </aside>
        ) : null}
      </div>

      <div className="app-room__controls" role="toolbar" aria-label="Contrôles de la visio">
        <div className="lk-button-group">
          <TrackToggle source={Track.Source.Microphone}>
            <span className="app-room__label">Micro</span>
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu kind="audioinput" aria-label="Choisir le micro" />
          </div>
        </div>
        <div className="lk-button-group">
          <TrackToggle source={Track.Source.Camera}>
            <span className="app-room__label">Caméra</span>
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu kind="videoinput" aria-label="Choisir la caméra" />
          </div>
        </div>
        {screenShareEnabled && canShareScreen() ? (
          <TrackToggle
            source={Track.Source.ScreenShare}
            captureOptions={{ audio: true, selfBrowserSurface: 'exclude' }}
          >
            <span className="app-room__label">Partager l’écran</span>
          </TrackToggle>
        ) : null}
        {chatEnabled && chat ? (
          <button
            type="button"
            className="lk-button"
            aria-pressed={panel === 'chat'}
            onClick={() => {
              chat.markRead();
              setPanel(panel === 'chat' ? null : 'chat');
            }}
          >
            <MessageSquare className="av-icon" aria-hidden="true" />
            <span className="app-room__label">Chat</span>
            {chat.unread > 0 && panel !== 'chat' ? (
              <span className="app-room__count">
                {chat.unread}
                <span className="av-visually-hidden"> non lu{chat.unread > 1 ? 's' : ''}</span>
              </span>
            ) : null}
          </button>
        ) : null}
        {sidePanel ? (
          <button
            type="button"
            className="lk-button"
            aria-pressed={panel === 'side'}
            onClick={() => {
              setPanel(panel === 'side' ? null : 'side');
            }}
          >
            <Users className="av-icon" aria-hidden="true" />
            <span className="app-room__label">{sidePanel.label}</span>
          </button>
        ) : null}
        <Button
          variant="danger"
          icon={<PhoneOff />}
          onClick={() => {
            onLeave({
              remoteIdentities: participants
                .filter((participant) => !participant.isLocal)
                .map((participant) => participant.identity),
            });
          }}
        >
          Quitter
        </Button>
      </div>
      <StartAudio label="Activer le son" className="app-room__start-audio" />
    </div>
  );
}

/** Grille des caméras ; un partage d'écran passe au premier plan, caméras en bandeau. */
function RoomStage() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const screenShare = tracks.find(
    (track) => isTrackReference(track) && track.source === Track.Source.ScreenShare,
  );
  const cameras = tracks.filter((track) => track.source === Track.Source.Camera);

  if (screenShare && isTrackReference(screenShare)) {
    return (
      <div className="app-room__stage">
        <FocusLayoutContainer>
          <CarouselLayout tracks={cameras}>
            <ParticipantTile />
          </CarouselLayout>
          <FocusLayout trackRef={screenShare} />
        </FocusLayoutContainer>
      </div>
    );
  }
  return (
    <div className="app-room__stage">
      <GridLayout tracks={cameras}>
        <ParticipantTile />
      </GridLayout>
    </div>
  );
}

/** Conséquence d'une déconnexion côté invité/hôte (la cause précise vient aussi du SSE). */
export function disconnectOutcome(
  reason: DisconnectReason | undefined,
): 'left' | 'removed' | 'ended' | 'lost' {
  if (reason === DisconnectReason.CLIENT_INITIATED) return 'left';
  if (reason === DisconnectReason.PARTICIPANT_REMOVED) return 'removed';
  if (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED)
    return 'ended';
  return 'lost';
}

export function ConnectionLost({ onRejoin }: { onRejoin: () => void }) {
  useEffect(() => {
    reportClientError('network_unrecoverable');
  }, []);
  return (
    <main className="app-centered">
      <Callout tone="warning" title="Impossible de se reconnecter." live="alert">
        <div className="av-stack av-stack--sm">
          <p>La connexion à la salle a été interrompue.</p>
          <div>
            <Button variant="secondary" onClick={onRejoin}>
              Rejoindre à nouveau
            </Button>
          </div>
        </div>
      </Callout>
    </main>
  );
}
